import { isRef, toRaw } from 'vue'

const isFunction = (val) => typeof val === 'function'
const isString = (val) => typeof val === 'string'

function formatComponentName (instance, Component, isRoot = false) {
  let name = getComponentName(Component)
  if (!name && Component.__file) {
    const match = Component.__file.match(/([^/\\]+)\.vue$/)
    if (match) {
      name = match[1]
    }
  }
  if (!name && instance && instance.parent) {
    name = `Anonymous`
  }
  if (name) {
    return name
  } else if (isRoot) {
    return `App`
  } else {
    return `Anonymous`
  }
}

function getComponentName (Component) {
  return (
    (isFunction(Component) && Component.displayName)
    || Component.name
    || Component.__name
    || Component._componentTag
  )
}

export function createWarnHandler (filter) {
  return (msg, instance, trace) => {
    if (filter && filter(msg, instance, trace)) return

    const componentTrace = getComponentTrace(instance)
    const formattedTrace = formatTrace(componentTrace)

    console.warn(`[Vue warn]: ${msg}`, ...formattedTrace)
  }
}

export function getComponentTrace (instance) {
  if (!instance) {
    return []
  }

  const internalInstance = instance.$ || instance

  const normalizedStack = []

  let current = internalInstance
  while (current) {
    const last = normalizedStack[0]
    if (last && last.vnode === current.vnode) {
      last.recurseCount++
    } else {
      normalizedStack.push({
        vnode: current.vnode,
        recurseCount: 0,
      })
    }
    current = current.parent
  }

  return normalizedStack
}

export function formatTrace (trace) {
  const logs = []
  trace.forEach((entry, i) => {
    logs.push(...(i === 0 ? [] : [`\n`]), ...formatTraceEntry(entry))
  })
  return logs
}

function formatTraceEntry ({ vnode, recurseCount }) {
  const postfix
    = recurseCount > 0 ? `... (${recurseCount} recursive calls)` : ``
  const isRoot = vnode.component ? vnode.component.parent === null : false
  const open = ` at <${formatComponentName(
    vnode.component,
    vnode.type,
    isRoot,
  )}`
  const close = `>` + postfix
  return vnode.props
    ? [open, ...formatProps(vnode.props), close]
    : [open + close]
}

function formatProps (props) {
  const res = []
  const keys = Object.keys(props)
  keys.slice(0, 3).forEach((key) => {
    res.push(...formatProp(key, props[key]))
  })
  if (keys.length > 3) {
    res.push(` ...`)
  }
  return res
}

function formatProp (key, value, raw) {
  if (isString(value)) {
    value = JSON.stringify(value)
    return raw ? value : [`${key}=${value}`]
  } else if (
    typeof value === 'number'
    || typeof value === 'boolean'
    || value === null
  ) {
    return raw ? value : [`${key}=${value}`]
  } else if (isRef(value)) {
    value = formatProp(key, toRaw(value.value), true)
    return raw ? value : [`${key}=Ref<`, value, `>`]
  } else if (isFunction(value)) {
    return [`${key}=fn${value.name ? `<${value.name}>` : ``}`]
  } else {
    value = toRaw(value)
    return raw ? value : [`${key}=`, value]
  }
}
